"use client";

import React, { useState } from "react";
import {
  Box,
  Collapse
} from "@mui/material";
import Link from "next/link";
import { DestinationAbbreviated, DestinationCardCarousel } from "@/types/dto/destinations-page/destination-page-types";
import { SectionHeader } from "@/components/common/section-header";
import RelatedCard from "./related-card";
import SeeMoreCard from "./see-more-card";

// TO DO - ONCE DESTINATIONS ARE NO LONGER MOCKED, REPLACE KEY NAME WITH ID

interface RelatedCardsRowProps {
  destinationCarousel: DestinationCardCarousel;
}

export const getColumnCount = (length: number, rowsPerCol: number) =>
  Math.ceil(length / rowsPerCol);

export const getResponsiveColumnWidth = (columnCount: number, breakpoint: "lg" | "sm" | "xs") => {
  // Container widths minus padding (approximate)
  const containerWidths = {
    lg: 1200, // Desktop max width in px
    sm: 768,  // Tablet width
    xs: 360   // Mobile width
  };
  
  const gap = breakpoint === "lg" ? 20 : 20; // 2rem gap in px
  const padding = breakpoint === "lg" ? 0 : 24; // Side padding in px
  
  const availableWidth = containerWidths[breakpoint] - (padding * 2);
  const totalGapWidth = (columnCount - 1) * gap;
  const availableForColumns = availableWidth - totalGapWidth;
  const calculatedWidth = Math.floor(availableForColumns / columnCount);
  
  // Minimum and maximum column widths (in px)
  const minWidth = 120;
  const maxWidth = 200;
  
  const finalWidth = Math.max(minWidth, Math.min(maxWidth, calculatedWidth));
  return `${finalWidth / 10}rem`; // Convert to rem (assuming 10px base)
};

const RelatedCardsRow: React.FC<RelatedCardsRowProps> = ({
  destinationCarousel
}) => {
  // Use mock data if no destinations provided
  const destinationsList = destinationCarousel.destinations; // .slice(0, 32);
  const destinationLength = destinationsList.length;
  const [expanded, setExpanded] = useState(false);

  const desktopRows = destinationLength > 32 ? 5 : 4;
  const tabletRows = destinationLength > 32 ? 10 : 8;
  const mobileRows = destinationLength > 32 ? 20 : destinationLength;
  const desktopColumns = getColumnCount(destinationLength, desktopRows);
  const tabletColumns = getColumnCount(destinationLength, tabletRows);
  const mobileColumns = getColumnCount(destinationLength, mobileRows);

  const hasMoreDestinations = destinationLength > 5;
  const visibleDestinations = hasMoreDestinations
    ? destinationsList.slice(0, 5)
    : destinationsList;

  // Determine card count for styling
  const displayedCardCount = hasMoreDestinations ? 6 : Math.min(destinationsList.length, 5);

  // Card styling based on count
  const getCardStyles = () => {
    const is5Plus = displayedCardCount >= 5;
    return {
      height: is5Plus ? 150 : 200,
      borderRadius: is5Plus ? "20px" : "10px",
      gradient: is5Plus
        ? "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.60) 100%)"
        : "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.60) 90.75%)"
    };
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Sort all destinations alphabetically for the drawer
  const alphabeticalDestinations = [...destinationsList].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <Box 
      sx={{ py: 4, px: { legacyMobile: 0, legacyTablet: "2rem", legacyDesktop: 0 }, maxWidth: 1440 }} 
      id="explore" 
      className="anchor-section"
      data-track="visibility"
      data-parent-container-type="carousel"
      data-module-name="Destination Card Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position="4"
      component="section"
    >
      <SectionHeader
        headerText={destinationCarousel?.basicInfo?.headerText || ""}
        copyText={destinationCarousel?.basicInfo?.copyText || ""}
        headerTestId="related-cards-header"
        copyTestId="related-cards-copy"
        sx={{ mb: 4 }}
      />

      {/* Destination Cards Container */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: { xs: "wrap", sm: "wrap", md: "wrap", lg: "nowrap" }
        }}
      >
        {visibleDestinations.map((destination: DestinationAbbreviated) => (
          <RelatedCard key={destination.name} destination={destination} getCardStyles={getCardStyles} />
        ))}

        {/* See More Card */}
        {hasMoreDestinations && (
          <SeeMoreCard
            expanded={expanded}
            onClick={handleToggleExpanded}
            cardStyles={getCardStyles()}
            data-testid="see-more-card"
          />
        )}
      </Box>


      {/* Expanded Destinations List */}
      <Collapse
        in={expanded}
        timeout={400}
        sx={{
          "& .MuiCollapse-wrapperInner": {
            transition: "opacity 300ms ease-in-out 100ms",
            opacity: expanded ? 1 : 0
          }
        }}
      >
        <Box
          sx={{
            transition: "opacity 300ms ease-in-out",
            opacity: expanded ? 1 : 0,
            padding: {
              legacyMobile: "2rem 2.4rem 4rem 2.4rem",
              legacyTablet: "2rem 2.4rem 4rem 2.4rem",
              legacyDesktop: "4rem 0 6rem 0"
            }
          }}>
          <Box
            data-testid="destinations-drawer"
            sx={{
              // Create columns that wrap vertically then horizontally
              display: "grid",
              gridAutoFlow: "column",
              justifyContent: "start",
              gridTemplateRows: {
                lg: `repeat(${desktopRows}, auto)`,
                sm: `repeat(${tabletRows}, auto)`,
                xs: `repeat(${mobileRows}, auto)`
              },
              gridTemplateColumns: {
                lg: `repeat(${desktopColumns}, ${getResponsiveColumnWidth(desktopColumns, "lg")})`,
                sm: `repeat(${tabletColumns}, ${getResponsiveColumnWidth(tabletColumns, "sm")})`,
                xs: `repeat(${mobileColumns}, ${getResponsiveColumnWidth(mobileColumns, "xs")})`
              },
              rowGap: { lg: "2rem", sm: "2.4rem", xs: "2rem" },
              columnGap: { lg: "2rem", sm: "2rem", xs: "2rem" },
              paddingLeft: { lg: "0", sm: "2.4rem", xs: "2.4rem" }

            }}
          >
            {alphabeticalDestinations.map((destination, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={(destination?.name || "destination") + index}                             sx={{
                  animation: expanded ? `fadeIn 300ms ease-out ${50 + index * 20}ms both` : "none",
                  "@keyframes fadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(10px)"
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)"
                    }
                  }
                }}
              >
                {destination?.url && 
                  <Link
                    href={destination.url}
                    style={{
                      textDecoration: "none",
                      color: "var(--theme-palette-secondary-main)",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "normal",
                      transition: "color 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    {destination.name} {/* {index + 1} */}
                  </Link>
                }
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RelatedCardsRow;